import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, PauseCircle, Plus, Trash2 } from "lucide-react";
import { api, DOC_LABELS, formatApiErrorDetail, money, shopTime, shopToday } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import CustomerHistory from "@/components/CustomerHistory";
import PhotoCapture from "@/components/PhotoCapture";
import NegotiatePriceDialog from "@/components/NegotiatePriceDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const KARATS = ["24K", "22K", "21K", "18K", "14K"];
// Truganina also trades silver — staff type the per-gram price by hand (no daily silver rate).
const SILVER = ["999", "925"];
const isSilver = (k) => SILVER.includes(k);
const BUYING_PAYMENT_METHODS = ["CASH", "BANK TRANSFER", "PAYID"];
const LOAN_PAYOUT_METHODS = ["CASH", "BANK TRANSFER", "PAYID", "CHEQUE", "CARD", "OTHER"];
const PAYMENT_METHODS = [
  "CASH",
  "BANK TRANSFER",
  "PAYID",
  "CARD DEBIT",
  "CARD",
  "KUBER PAYID",
  "KUBER INSTALLMENTS",
  "KUBER APPLE / GOOGLE PAY",
  "KUBER AFTERPAY",
  "KUBER KLARNA",
  "KUBER ZIP PAY",
];

// Kuber caps an installments plan at $10,000.
const KUBER_INSTALLMENT_LIMIT = 10000;

// Methods that settle to the shop straight away, so the invoice is paid on save.
// Every Kuber method is excluded — the funds are only confirmed by Kuber later,
// so staff tick "Paid in full now" themselves once the money shows up.
const SETTLED_ON_SAVE = PAYMENT_METHODS.filter((m) => !m.startsWith("KUBER"));

// Higher-fee Kuber methods: if one of these actually comes in but the invoice is
// filed as a cheaper method (CARD/Cash), the printed handling fee understates what
// the customer was charged — so we gently prompt staff to switch.
const KUBER_FEE_METHODS = [
  "KUBER AFTERPAY",
  "KUBER KLARNA",
  "KUBER ZIP PAY",
  "KUBER INSTALLMENTS",
  "KUBER APPLE / GOOGLE PAY",
];

// Platform handling fee — paused by the owner (flip to true to show/charge it again).
const PLATFORM_FEE_ENABLED = false;

// Fee per method is a percentage plus a fixed amount per transaction.
const feeOf = (rates, method) => rates?.[method] || { pct: 0, fixed: 0 };
const feeLabel = (rates, method) => {
  if (!PLATFORM_FEE_ENABLED) return "";
  const { pct, fixed } = feeOf(rates, method);
  if (!pct && !fixed) return " - no provider fee";
  const bits = [];
  if (pct) bits.push(`${pct}%`);
  if (fixed) bits.push(`$${Number(fixed).toFixed(2)}`);
  return ` - provider keeps ${bits.join(" + ")}`;
};
// Must agree with Python's round(x, 2) on the server to the cent, or "Fill balance"
// lands a cent out and the invoice saves with a stray credit or balance owing.
// Multiplying by 100 cannot be trusted: 2.675 * 100 comes out as exactly 267.5 in
// floating point even though the stored number is really 2.674999..., so a half-up
// rounding there gives 2.68 where Python gives 2.67. toFixed(20) exposes the number as
// it is actually held, so the true remainder decides, and a dead-heat half goes to the
// nearest EVEN cent exactly as Python does. (Same function in DocumentView.jsx.)
const round2 = (n) => {
  const x = Number(n) || 0;
  if (!Number.isFinite(x)) return 0;
  const [intPart, frac = ""] = Math.abs(x).toFixed(20).split(".");
  const rest = frac.slice(2).replace(/0+$/, "");
  let cents = Number(intPart) * 100 + Number(frac.slice(0, 2).padEnd(2, "0"));
  if (rest === "5") cents += cents % 2 === 1 ? 1 : 0;
  else if (rest.startsWith("5") || rest > "5") cents += 1;
  return (x < 0 ? -cents : cents) / 100;
};
const feeOn = (rates, amount, method) => {
  const amt = Number(amount) || 0;
  if (!amt) return 0;
  const { pct, fixed } = feeOf(rates, method);
  // Rounded per payment, exactly as the server charges it.
  return round2((amt * Number(pct || 0)) / 100 + Number(fixed || 0));
};

// Where the money is actually taken, so the figure on screen reads like an instruction.
const TERMINAL_METHODS = ["CARD", "CARD DEBIT"];
const collectWording = (method) => {
  if (TERMINAL_METHODS.includes(method)) return "on the card terminal";
  if (method === "CASH") return "in cash";
  if (method === "PAYID" || method === "BANK TRANSFER") return "by transfer";
  if (method === "CHEQUE") return "by cheque";
  if (method?.startsWith("KUBER")) return "through Kuber";
  return method ? `by ${method.toLowerCase()}` : "";
};

const emptyItem = () => ({
  qty: 1,
  description: "",
  karat: "22K",
  gross_weight: "",
  less_weight: "",
  weight: "",
  stone_cost: "",
  labour_pct: "",
  set_price: "",
  rate: "",
  amount: "",
});

// Staff work down a ruled book, so a new sales docket opens with six blank rows.
// Blank rows are dropped on save and the printed invoice rules its own blank lines.
const START_ROWS = { tax_invoice: 6, gold_bullion: 6, buying_contract: 1, loan: 1 };
const blankRows = (type) =>
  Array.from({ length: START_ROWS[type] || 1 }, () => emptyItem());

const COUNTRY_CODES = [
  ["+61", "Australia"],
  ["+64", "New Zealand"],
  ["+91", "India"],
  ["+44", "United Kingdom"],
  ["+1", "USA / Canada"],
  ["+65", "Singapore"],
  ["+60", "Malaysia"],
  ["+971", "UAE"],
  ["+92", "Pakistan"],
  ["+94", "Sri Lanka"],
  ["+977", "Nepal"],
  ["+63", "Philippines"],
  ["+62", "Indonesia"],
  ["+66", "Thailand"],
  ["+86", "China"],
  ["+81", "Japan"],
  ["+27", "South Africa"],
  ["+254", "Kenya"],
  ["+679", "Fiji"],
];

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
      {label}
    </Label>
    {children}
  </div>
);

const inputCls = "h-10 rounded-none";

export default function DocumentFormRoute() {
  // Remount on route change so switching invoice types starts a clean form.
  const { docType, id } = useParams();
  return <DocumentForm key={id || docType} />;
}

function DocumentForm() {
  const { docType: routeDocType, id: editId } = useParams();
  const [searchParams] = useSearchParams();
  const reorderFrom = searchParams.get("from");
  const { branch, user } = useAuth();
  const canSetGst = ["admin", "it_admin", "manager"].includes(user?.role);
  const navigate = useNavigate();
  const [catalogue, setCatalogue] = useState([]);
  const [goldRates, setGoldRates] = useState({});
  const [feeRates, setFeeRates] = useState({});
  const [saving, setSaving] = useState(false);
  const [docId, setDocId] = useState(null);
  const [docType, setDocType] = useState(routeDocType || "tax_invoice");
  const [existing, setExisting] = useState(null);
  const [amendReason, setAmendReason] = useState("");
  const [rateChoice, setRateChoice] = useState(null);
  const [parkedInfo, setParkedInfo] = useState(null);
  const [autoSavedAt, setAutoSavedAt] = useState(null);
  const [confirmDetails, setConfirmDetails] = useState(false);

  const isTax = docType === "tax_invoice";
  const isBullion = docType === "gold_bullion";
  const isLoan = docType === "loan";
  const isBuying = docType === "buying_contract";
  // Silver karats (999/925) are offered at both branches, on sales and buying contracts.
  const karats = [...KARATS, ...SILVER];

  const [form, setForm] = useState({
    customer_name: "",
    customer_phone: "",
    customer_country_code: "+61",
    customer_address: "",
    customer_email: "",
    customer_birthday: "",
    payment_method: "",
    notes: "",
    deposit: 0,
    deposit_method: "CASH",
    wear_tear_explained: false,
    bullion_metal: "GOLD",
    mark_paid: true,
    gst_rate: 10,
    platform_fee_override: "",
    rate_date: "",
  });
  const [items, setItems] = useState(() => blankRows(routeDocType || "tax_invoice"));
  const [negotiateOpen, setNegotiateOpen] = useState(false);
  const [pendingDiscount, setPendingDiscount] = useState(null);
  const [payments, setPayments] = useState([]);
  const [tradeIns, setTradeIns] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [accountDeposits, setAccountDeposits] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [kyc, setKyc] = useState(null);
  const [cash, setCash] = useState(null);
  const [loan, setLoan] = useState({
    loan_amount: "",
    paid_to_customer_by: "CASH",
    payout_reference: "",
    interest_rate: 7,
    id_number: "",
    min_months: 1,
    max_months: 2,
    start_date: shopToday(),
    end_date: "",
    description: "",
    weight: "",
  });
  const [buying, setBuying] = useState({
    date_of_birth: "",
    id_number: "",
    state_of_issue: "VIC",
    expiry: "",
    dealer_name: "",
    disposal_goods: "",
    disposal_date: "",
    disposal_price: "",
    disposal_buyer: "",
  });

  useEffect(() => {
    api
      .get("/catalogue", { params: { category: isBullion ? "bullion" : "jewellery" } })
      .then(({ data }) => setCatalogue(data))
      .catch(() => setCatalogue([]));
  }, [isBullion]);

  useEffect(() => {
    api
      .get("/gold-rates", { params: { branch } })
      .then(({ data }) =>
        setGoldRates(
          Object.fromEntries(Object.entries(data).map(([k, v]) => [k, v.rate]))
        )
      )
      .catch(() => setGoldRates({}));
    api
      .get("/settings/platform-fees")
      .then(({ data }) => setFeeRates(data))
      .catch(() => setFeeRates({}));
    // Pre-fill the dealer for buying contracts from this store's settings.
    api
      .get("/settings/branches")
      .then(({ data }) => {
        const dealer = data?.[branch]?.dealer_name;
        if (dealer) setBuying((b) => (b.dealer_name ? b : { ...b, dealer_name: dealer }));
      })
      .catch(() => {});
  }, [branch]);

  useEffect(() => {
    if (editId) return;
    setItems(blankRows(docType));
  }, [docType, editId]);

  // The number is reserved the moment the form opens, so staff can read it out to the
  // customer before anything is typed. It is held by a blank parked docket so the
  // numbering never has an unexplained gap.
  const [reservedNumber, setReservedNumber] = useState(null);
  const [kuberWaiting, setKuberWaiting] = useState([]);
  // Warn staff if a Kuber payment is already sitting unmatched for this invoice number,
  // so they don't record it again by hand (it attaches itself on save/issue).
  useEffect(() => {
    const num = existing?.doc_number ?? reservedNumber;
    if (!num) {
      setKuberWaiting([]);
      return undefined;
    }
    let cancel = false;
    api
      .get("/kuber/payments/waiting", { params: { doc_number: String(num) } })
      .then(({ data }) => {
        if (!cancel) setKuberWaiting(data.waiting || []);
      })
      .catch(() => {});
    return () => {
      cancel = true;
    };
  }, [existing?.doc_number, reservedNumber]);
  useEffect(() => {
    if (editId || docId) return;
    let cancelled = false;
    api
      .post("/documents/draft", { doc_type: docType, branch })
      .then(({ data }) => {
        if (cancelled) return;
        setDocId(data.id);
        setReservedNumber(data.doc_number);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId, docType, branch]);

  // Load an existing record for amending or to resume a parked docket.
  useEffect(() => {
    if (!editId) return;
    api
      .get(`/documents/${editId}`)
      .then(({ data }) => {
        setExisting(data);
        setDocId(data.id);
        setDocType(data.doc_type);
        setForm({
          customer_name: data.customer_name || "",
          customer_phone: data.customer_phone || "",
          customer_country_code: data.customer_country_code || "+61",
          customer_address: data.customer_address || "",
          customer_email: data.customer_email || "",
          customer_birthday: data.customer_birthday || "",
          payment_method: data.payment_method || "",
          notes: data.notes || "",
          deposit: data.deposit || 0,
          deposit_method: data.deposit_method || "CASH",
          wear_tear_explained: data.wear_tear_explained || false,
          bullion_metal: data.bullion_metal || "GOLD",
          mark_paid: false,
          gst_rate: data.gst_rate ?? 10,
          platform_fee_override:
            data.platform_fee_override === null || data.platform_fee_override === undefined
              ? ""
              : data.platform_fee_override,
          rate_date: data.rate_date || "",
        });
        setItems(
          (data.items || []).length
            ? data.items.map((it) => ({ ...emptyItem(), ...it, booked_rate: it.rate || 0 }))
            : blankRows(data.doc_type)
        );
        setPayments(
          (data.payments || []).map((p) => ({
            method: p.method,
            amount: p.amount,
            label: p.label || "",
          }))
        );
        setTradeIns(
          (data.trade_ins || []).map((t) => ({
            description: t.description || "OLD GOLD TRADING",
            weight: t.weight || 0,
            rate: t.rate || 0,
            wear_tear_pct: t.wear_tear_pct || 0,
            amount: t.amount || 0,
          }))
        );
        setDeposits(
          (data.deposits || []).length
            ? data.deposits.map((d) => ({
                entry_date: d.entry_date || "",
                amount: d.amount,
                method: d.method || "CASH",
                note: d.note || "",
                ledger_id: d.ledger_id || "",
              }))
            : data.deposit
              ? [{
                  entry_date: "",
                  amount: data.deposit,
                  method: data.deposit_method || "CASH",
                  note: "",
                }]
              : []
        );
        setAttachments(data.attachments || []);
        if (data.loan) setLoan((l) => ({ ...l, ...data.loan }));
        if (data.buying) setBuying((b) => ({ ...b, ...data.buying }));
        if (data.status === "parked") setParkedInfo({ by: data.parked_by, at: data.parked_at });
      })
      .catch(() => toast.error("Could not load that record"));
  }, [editId]);

  useEffect(() => {
    if (!reorderFrom) return;
    api
      .get(`/documents/${reorderFrom}`)
      .then(({ data }) => {
        setForm((f) => ({
          ...f,
          customer_name: data.customer_name || "",
          customer_phone: data.customer_phone || "",
          customer_country_code: data.customer_country_code || "+61",
          customer_address: data.customer_address || "",
          customer_email: data.customer_email || "",
          customer_birthday: data.customer_birthday || "",
          payment_method: data.payment_method || f.payment_method,
          notes: data.notes || "",
          deposit: 0,
        }));
        if (data.items?.length) {
          setItems(
            data.items.map((it) => ({
              qty: it.qty || 1,
              description: it.description || "",
              karat: it.karat || "22K",
              gross_weight: it.weight || 0,
              less_weight: 0,
              weight: it.weight || 0,
              stone_cost: it.stone_cost || 0,
              labour_pct: it.labour_pct || 0,
              set_price: it.set_price || 0,
              rate: 0,
              amount: 0,
            }))
          );
        }
        if (data.loan) setLoan((l) => ({ ...l, ...data.loan, status: "active" }));
        if (data.buying) setBuying((b) => ({ ...b, ...data.buying }));
        toast.success(`Copied from #${data.doc_number} — rates use today's prices`);
      })
      .catch(() => toast.error("Could not load that record to copy"));
  }, [reorderFrom]);

  const rateFor = (karat) => Number(goldRates[karat]) || 0;

  // Show today's rate in the row as soon as it is known, so staff see what is charged.
  useEffect(() => {
    if (!isTax || !Object.keys(goldRates).length) return;
    setItems((prev) =>
      prev.map((it) =>
        Number(it.rate) || isSilver(it.karat) ? it : { ...it, rate: Number(goldRates[it.karat]) || 0 }
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goldRates, isTax]);

  // NWT is never typed — always GWT less the stone weight.
  const netWeight = (it) =>
    it.gross_weight
      ? Math.round(((Number(it.gross_weight) || 0) - (Number(it.less_weight) || 0)) * 1000) / 1000
      : Number(it.weight) || 0;

  const badWeightRow = items.findIndex(
    (it) => Number(it.gross_weight) > 0 && Number(it.less_weight) > Number(it.gross_weight)
  );

  // Old gold taken in part exchange, less any wear-and-tear deduction.
  const tradeInValue = (t) => {
    const gross = (Number(t.weight) || 0) * (Number(t.rate) || 0);
    return round2(
      gross ? gross - (gross * (Number(t.wear_tear_pct) || 0)) / 100 : Number(t.amount) || 0
    );
  };

  const todayLabel = () => {
    const [y, m, d] = shopToday().split("-");
    return `${d}/${m}/${y}`;
  };

  const pullAccountDeposits = () => {
    const existing = new Set(deposits.map((d) => d.ledger_id).filter(Boolean));
    const rows = accountDeposits
      .filter((d) => !existing.has(d.entry_id))
      .map((d) => ({
        entry_date: d.date || "",
        amount: d.amount,
        method: d.method || "CASH",
        note: d.note || "FROM ACCOUNT",
        ledger_id: d.entry_id,
      }));
    if (!rows.length) {
      toast.info("Those deposits are already on this invoice");
      return;
    }
    setDeposits([...deposits, ...rows]);
    toast.success(`${rows.length} deposit${rows.length > 1 ? "s" : ""} added`);
  };

  const lineAmount = (it) => {
    if (!isTax) return round2((Number(it.qty) || 0) * (Number(it.rate) || 0));
    const base = netWeight(it) * (Number(it.rate) || rateFor(it.karat));
    // Rounded per line, exactly as the server stores it — otherwise the balance
    // can end up a cent out.
    return round2(
      base +
        (base * (Number(it.labour_pct) || 0)) / 100 +
        (Number(it.stone_cost) || 0) +
        (Number(it.set_price) || 0)
    );
  };

  const totals = useMemo(() => {
    const itemsTotal = round2(items.reduce((sum, it) => sum + lineAmount(it), 0));
    const activePayments = payments.filter((p) => Number(p.amount) > 0);
    let feeRate = 0;
    let platformFee = 0;
    // A card/Afterpay/Kuber deposit costs the shop the same merchant fee.
    const depositTotal = round2(deposits.reduce((sum, d) => sum + (Number(d.amount) || 0), 0));
    const depositFee = round2(
      deposits.reduce((sum, d) => sum + feeOn(feeRates, d.amount, d.method), 0)
    );
    if (activePayments.length || depositFee) {
      platformFee = round2(
        depositFee +
          activePayments.reduce((s, p) => s + feeOn(feeRates, p.amount, p.method), 0)
      );
      feeRate = itemsTotal ? (platformFee / itemsTotal) * 100 : 0;
    } else {
      platformFee = feeOn(feeRates, itemsTotal, form.payment_method);
      feeRate = itemsTotal ? (platformFee / itemsTotal) * 100 : 0;
    }
    if (form.platform_fee_override !== "" && form.platform_fee_override !== undefined) {
      platformFee = Math.max(Number(form.platform_fee_override) || 0, 0);
      feeRate = itemsTotal ? (platformFee / itemsTotal) * 100 : 0;
    }
    if (isBuying || !PLATFORM_FEE_ENABLED) {
      platformFee = 0;
      feeRate = 0;
    }
    const subtotal = round2(itemsTotal);
    const gstRate = isTax ? Number(form.gst_rate) || 0 : 0;
    const gst = round2((subtotal * gstRate) / 100);
    // The handling fee is the provider's, added and kept at their end. It is shown for
    // disclosure only and never forms part of what the customer owes the shop.
    const total = round2(subtotal + gst);
    const tradeInTotal = round2(tradeIns.reduce((s, t) => s + tradeInValue(t), 0));
    const paid = round2(
      activePayments.reduce((s, p) => s + Number(p.amount), 0) + depositTotal + tradeInTotal
    );
    return {
      itemsTotal,
      feeRate,
      platformFee: round2(platformFee),
      subtotal,
      gst,
      total,
      tradeInTotal,
      depositTotal,
      weight: items.reduce((s, it) => s + netWeight(it), 0),
      paid,
      balance: round2(total - paid),
      // What staff actually take. Deposits count too — they are money being collected
      // right now, and staff were left with no figure when a sale was taken entirely on
      // deposit rows. Each amount is what the shop receives; the provider's fee is added
      // at their end and must never be keyed in on top.
      chargeRows: [
        ...deposits
          .filter((d) => Number(d.amount) > 0)
          .map((d) => ({ method: d.method, amount: round2(d.amount),
                         fee: feeOn(feeRates, d.amount, d.method), kind: "Deposit" })),
        ...activePayments.map((p) => ({ method: p.method, amount: round2(p.amount),
                                        fee: feeOn(feeRates, p.amount, p.method),
                                        kind: "" })),
      ],
      chargeTotal: round2(
        deposits.reduce((s, d) => s + (Number(d.amount) || 0), 0) +
          activePayments.reduce((s, p) => s + (Number(p.amount) || 0), 0)
      ),
      chargeFee: round2(
        deposits.reduce((s, d) => s + feeOn(feeRates, d.amount, d.method), 0) +
          activePayments.reduce((s, p) => s + feeOn(feeRates, p.amount, p.method), 0)
      ),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, payments, tradeIns, deposits, form.gst_rate,
      form.payment_method, form.platform_fee_override, feeRates, goldRates, isTax, isBuying]);

  // Add a tidy "Negotiated discount" line so the invoice total lands on the agreed price.
  // Uses the existing line engine (a line with no weight and a negative set price), so no
  // calculation changes — and records the discount (with the reason) in the Audit Trail.
  const applyNegotiatedDiscount = ({ agreedIncGst, discountExGst, reason }) => {
    const discountRow = {
      ...emptyItem(),
      description: "Negotiated discount",
      karat: "",
      gross_weight: "",
      less_weight: "",
      weight: "",
      rate: "",
      stone_cost: "",
      labour_pct: "",
      set_price: -round2(discountExGst),
    };
    // Drop it into the first blank row so it sits next to the goods; else add a new row.
    setItems((prev) => {
      const blankIdx = prev.findIndex((it) => !it.description.trim());
      if (blankIdx === -1) return [...prev, discountRow];
      const next = [...prev];
      next[blankIdx] = discountRow;
      return next;
    });
    setNegotiateOpen(false);
    toast.success("Negotiated discount added as a line");
    // Remember the deal; it is written to the Audit Trail only when the invoice is saved,
    // so abandoned or edited dockets never leave phantom discounts in the report.
    setPendingDiscount({
      original_total: totals.total,
      agreed_total: round2(agreedIncGst),
      discount_ex_gst: round2(discountExGst),
      reason: reason.trim(),
    });
  };

  // Record the negotiated discount in the Audit Trail once the invoice is actually saved.
  // Amounts are derived from the saved invoice (all "Negotiated discount" lines and the
  // final total), so stacked or hand-edited discounts are always reported correctly.
  const logPendingDiscount = async (saved) => {
    if (!pendingDiscount) return;
    const discountEx = round2(
      items
        .filter(
          (it) =>
            (it.description || "").trim() === "Negotiated discount" && Number(it.set_price) < 0
        )
        .reduce((s, it) => s + Math.abs(Number(it.set_price) || 0), 0)
    );
    if (discountEx <= 0) {
      setPendingDiscount(null);
      return;
    }
    const rate = Number(form.gst_rate) || 0;
    const agreedTotal = totals.total;
    const origSubtotal = round2(totals.subtotal + discountEx);
    const originalTotal = round2(origSubtotal + round2((origSubtotal * rate) / 100));
    try {
      await api.post("/calculator/discount-applied", {
        branch,
        doc_number: saved?.doc_number ?? existing?.doc_number ?? reservedNumber ?? null,
        doc_type: docType,
        customer_name: form.customer_name || "",
        original_total: originalTotal,
        agreed_total: agreedTotal,
        discount_ex_gst: discountEx,
        reason: pendingDiscount.reason || "",
      });
    } catch (e) {
      /* the sale is saved regardless; the report row is best-effort */
    }
    setPendingDiscount(null);
  };

  // Open the Ex-GST Calculator in a new tab, carrying this invoice's number and totals.
  const openCalculator = () => {
    const num = existing?.doc_number ?? reservedNumber ?? "";
    const gstVal = form.gst_rate === "" || form.gst_rate == null ? 10 : Number(form.gst_rate);
    const qs = new URLSearchParams({
      invoice: num ? String(num) : "",
      total: String(totals.total || 0),
      subtotal: String(totals.subtotal || 0),
      gst: String(gstVal),
    }).toString();
    window.open(`/calculator?${qs}`, "_blank");
  };

  // The end date follows the maximum months, unless staff have typed their own.
  useEffect(() => {
    if (!isLoan || !loan.start_date || loan.end_date_edited) return;
    const months = Math.max(parseInt(loan.max_months, 10) || 1, 1);
    const d = new Date(`${loan.start_date}T00:00:00`);
    // Clamp to the last day of the target month. Adding months to the 31st otherwise
    // overflows — 31 Jan + 1 month became 3 March, handing the customer three extra
    // days on the agreement.
    const startDay = d.getDate();
    d.setDate(1);
    d.setMonth(d.getMonth() + months);
    const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
    d.setDate(Math.min(startDay, lastDay));
    // Built from the local parts on purpose. toISOString() converts to UTC first, which
    // in Melbourne (UTC+10/+11) rolls the date back a day — every loan agreement was
    // ending one day early.
    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${
      String(d.getDate()).padStart(2, "0")}`;
    if (iso !== loan.end_date) setLoan((l) => ({ ...l, end_date: iso }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoan, loan.start_date, loan.max_months]);

  const interestPerMonth =
    ((Number(loan.loan_amount) || 0) * (Number(loan.interest_rate) || 0)) / 100;

  // What the shop still needs to receive to clear the invoice. The provider's fee is
  // added on at their end and is never part of this figure, so there is nothing to
  // work backwards through any more.
  const fillBalance = (method, current = 0) => {
    const outstanding = round2((Number(totals.balance) || 0) + (Number(current) || 0));
    return outstanding > 0 ? outstanding : 0;
  };

  // A "Fill balance" amount is worked out from the figures at the moment it was pressed.
  // If anything changes afterwards — an item, a trade-in, the rates, the GST — that
  // amount is stale and silently leaves a few cents owing (prod invoice 211260 ended up
  // 9c short exactly this way). So whenever the numbers move, re-fill the filled row.
  useEffect(() => {
    const i = payments.findIndex((p) => p.filled && Number(p.amount) > 0);
    if (i < 0) return;
    const want = fillBalance(payments[i].method, payments[i].amount);
    if (!want) return;
    if (Math.abs(round2(want) - round2(payments[i].amount)) < 0.005) return;
    setPayments((prev) =>
      prev.map((x, j) => (j === i ? { ...x, amount: want } : x))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totals.balance, totals.total, payments, deposits, tradeIns, feeRates]);

  // The printed invoice gives a line description 56mm before it wraps onto a second
  // line, which makes the row taller and eats into how much fits at full size. The
  // backend sends the real font metrics so this hint matches what actually prints.
  const [descMetrics, setDescMetrics] = useState(null);
  useEffect(() => {
    api
      .get("/pdf/description-metrics")
      .then(({ data }) => setDescMetrics(data))
      .catch(() => setDescMetrics(null));
  }, []);

  const descriptionWraps = (text) => {
    if (!descMetrics || !text) return false;
    const { char_widths: w, fallback, max_width: max } = descMetrics;
    let total = 0;
    for (const ch of String(text).toUpperCase()) {
      total += w[ch] ?? fallback;
      if (total > max) return true;
    }
    return false;
  };

  const updateItem = (idx, patch) =>
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));

  const changeWeight = (idx, value) => {
    const it = items[idx];
    const booked = Number(it.booked_rate) || 0;
    const today = rateFor(it.karat);
    if (isIssued && booked && today && booked !== today && !it.rate_decided) {
      setRateChoice({ idx, weight: value, booked, today });
      return;
    }
    updateItem(idx, { gross_weight: value });
  };

  const padWeight = (idx, field, value, extra = {}) => {
    if (value === "" || value === null || isNaN(Number(value))) return;
    updateItem(idx, { [field]: Number(value).toFixed(3), ...extra });
  };

  const applyRatesToAll = async () => {
    setItems((prev) => prev.map((it) => ({ ...it, rate: isSilver(it.karat) ? it.rate : rateFor(it.karat) })));
    toast.success("Today's gold rates applied");
  };

  const buildPayload = (park) => ({
    customer_country_code: form.customer_country_code || "+61",
    doc_type: docType,
    branch,
    ...form,
    deposit: 0,
    deposits: deposits
      .filter((d) => Number(d.amount) > 0)
      .map((d) => ({
        entry_date: d.entry_date || "",
        amount: Number(d.amount),
        method: d.method || "CASH",
        note: d.note || "",
        ledger_id: d.ledger_id || "",
      })),
    platform_fee_override:
      form.platform_fee_override === "" || form.platform_fee_override === undefined
        ? null
        : Number(form.platform_fee_override),
    gst_rate: isTax ? Number(form.gst_rate) || 0 : 0,
    gold_rates: goldRates,
    park,
    amend_reason: amendReason,
    payments: payments
      .filter((p) => Number(p.amount) > 0)
      .map((p) => ({ method: p.method, amount: Number(p.amount), label: p.label || "" })),
    trade_ins: tradeIns
      .filter((t) => tradeInValue(t) > 0)
      .map((t) => ({
        description: t.description || "OLD GOLD TRADING",
        weight: Number(t.weight) || 0,
        rate: Number(t.rate) || 0,
        wear_tear_pct: Number(t.wear_tear_pct) || 0,
        amount: Number(tradeInValue(t).toFixed(2)),
      })),
    attachments: attachments.map((a) => ({
      file_id: a.file_id,
      kind: a.kind || "id_photo",
      label: a.label || "",
      filename: a.filename || "",
      content_type: a.content_type || "",
      size: a.size || 0,
    })),
    items: isLoan
      ? []
      : items
          .filter((it) => it.description.trim())
          .map((it) => ({
            qty: Number(it.qty) || 1,
            description: it.description,
            karat: it.karat,
            gross_weight: Number(it.gross_weight) || 0,
            less_weight: Number(it.less_weight) || 0,
            weight: netWeight(it),
            stone_cost: Number(it.stone_cost) || 0,
            labour_pct: Number(it.labour_pct) || 0,
            set_price: Number(it.set_price) || 0,
            rate: Number(it.rate) || rateFor(it.karat),
            amount: lineAmount(it),
          })),
    loan: isLoan
      ? {
          ...loan,
          loan_amount: Number(loan.loan_amount) || 0,
          interest_rate: Number(loan.interest_rate) || 0,
          min_months: Number(loan.min_months) || 1,
          max_months: Number(loan.max_months) || 1,
          status: loan.status || "active",
        }
      : null,
    buying: isBuying
      ? { ...buying, disposal_price: Number(buying.disposal_price) || 0 }
      : null,
  });

  const canSave = form.customer_name.trim() && form.customer_phone.trim();

  // Does this customer already have an AML/KYC form on file?
  useEffect(() => {
    const phone = form.customer_phone.replace(/\D/g, "");
    if (phone.length < 8) {
      setKyc(null);
      setCash(null);
      return;
    }
    let cancelled = false;
    api
      .get("/cash-watch/status", { params: { phone: form.customer_phone } })
      .then(({ data }) => !cancelled && setCash(data))
      .catch(() => !cancelled && setCash(null));
    api
      .get(`/customers/${phone}/unapplied-deposits`)
      .then(({ data }) => !cancelled && setAccountDeposits(data || []))
      .catch(() => !cancelled && setAccountDeposits([]));
    if (!isBuying) return;
    api
      .get("/kyc/status", { params: { phone: form.customer_phone } })
      .then(({ data }) => {
        if (cancelled) return;
        setKyc(data);
        if (!data?.id) return;
        // Fill the seller's details straight from the KYC form they submitted.
        api.get(`/kyc/${data.id}`).then(({ data: rec }) => {
          if (cancelled || !rec) return;
          setForm((f) => ({
            ...f,
            customer_name: f.customer_name || rec.customer_name || "",
            customer_address: f.customer_address || rec.address || "",
            customer_email: f.customer_email || rec.email || "",
          }));
          setBuying((b) => ({
            ...b,
            id_number: b.id_number || rec.licence_number || rec.passport_number || "",
            state_of_issue: b.state_of_issue || rec.licence_state || "",
            expiry: b.expiry || rec.licence_expiry || rec.passport_expiry || "",
          }));
        }).catch(() => {});
      })
      .catch(() => !cancelled && setKyc(null));
    return () => {
      cancelled = true;
    };
  }, [form.customer_phone, isBuying]);
  const isParked = existing?.status === "parked" || (!existing && !!docId);
  const isIssued = existing?.status === "active";

  // Nothing entered is ever lost: the docket keeps saving itself while staff work.
  useEffect(() => {
    if (!canSave || saving || isIssued) return;
    const t = setTimeout(async () => {
      try {
        if (docId) {
          await api.put(`/documents/${docId}`, buildPayload(true));
        } else {
          const { data } = await api.post("/documents", buildPayload(true));
          setDocId(data.id);
          setParkedInfo({ by: data.parked_by, at: data.parked_at });
        }
        setAutoSavedAt(new Date());
      } catch (e) {
        /* keep typing — the next tick retries */
      }
    }, 2500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, items, payments, loan, buying, canSave, docId, isIssued]);

  const parkAndServeNext = async () => {
    if (!canSave) {
      toast.error("Customer name and phone number are needed before parking");
      return;
    }
    setSaving(true);
    try {
      if (docId) await api.put(`/documents/${docId}`, buildPayload(true));
      else {
        const { data } = await api.post("/documents", buildPayload(true));
        setDocId(data.id);
      }
      toast.success("Docket parked — serve the next customer");
      navigate("/parked");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_name.trim()) {
      toast.error("Customer name is required");
      return;
    }
    if (!form.customer_phone.trim()) {
      toast.error("Customer phone number is required");
      return;
    }
    if (isIssued && !amendReason.trim()) {
      toast.error("Please give a reason for amending this issued record");
      return;
    }
    if (isLoan && !loan.paid_to_customer_by) {
      toast.error("Record how the loan was paid to the customer");
      return;
    }
    if (badWeightRow >= 0) {
      toast.error(
        `Stone weight cannot be more than the gross weight on line ${badWeightRow + 1}`
      );
      return;
    }
    if (isBuying && attachments.length === 0) {
      toast.error("Please capture or attach at least one customer ID photo or document");
      return;
    }
    setSaving(true);
    try {
      let saved;
      if (docId) {
        const { data } = await api.put(`/documents/${docId}`, buildPayload(false));
        saved = data;
        if (data.status === "parked") {
          const res = await api.post(`/documents/${docId}/finalise`);
          saved = res.data;
        }
      } else {
        const { data } = await api.post("/documents", buildPayload(false));
        saved = data;
      }
      if (form.mark_paid && !isLoan && (saved.balance || 0) > 0.005) {
        try {
          const { data } = await api.post(`/documents/${saved.id}/pay-full`, {
            method: form.payment_method,
          });
          saved = data || saved;
        } catch (err) {
          toast.error("Saved, but could not mark it paid — settle it on the record");
        }
      }
      await logPendingDiscount(saved);
      toast.success(
        isIssued
          ? `${DOC_LABELS[docType]} #${saved.doc_number} amended`
          : `${DOC_LABELS[docType]} #${saved.doc_number} saved`
      );
      navigate(`/documents/${saved.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="fade-up" data-testid="document-form">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-[#B38F4E]">
            {branch}
            {existing ? ` · #${existing.doc_number}` : reservedNumber ? ` · #${reservedNumber}` : ""}
          </p>
          <h1 className="mt-2 text-3xl font-bold sm:text-4xl">
            {isIssued ? `Amend ${DOC_LABELS[docType]}` : DOC_LABELS[docType]}
          </h1>
          {autoSavedAt && !isIssued && (
            <p className="mt-1 text-xs text-muted-foreground" data-testid="autosave-indicator">
              Draft saved automatically at {shopTime(autoSavedAt)} — nothing will be lost
            </p>
          )}
          {!existing && reservedNumber && (
            <p className="mt-1 text-xs font-semibold text-[#166534]" data-testid="reserved-number-note">
              Number #{reservedNumber} is held for this sale — safe to give to the customer now
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {!isIssued && (
            <Button
              type="button"
              variant="outline"
              disabled={saving}
              onClick={parkAndServeNext}
              data-testid="park-invoice-button"
              className="h-11 rounded-none px-5"
            >
              <PauseCircle className="mr-1.5 h-4 w-4" /> Park &amp; serve next
            </Button>
          )}
          <Button
            type="submit"
            disabled={saving}
            data-testid="save-document-button"
            className="h-11 rounded-none bg-[#C5A059] px-6 font-semibold text-[#0A0A0A] hover:bg-[#B38F4E]"
          >
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isIssued ? "Save amendment" : "Save & Preview"}
          </Button>
        </div>
      </div>

      {parkedInfo && !isIssued && (
        <div
          className="mt-4 border border-[#C5A059] bg-[#FBF8F2] px-4 py-2.5 text-sm"
          data-testid="parked-banner"
        >
          Parked docket — held by {parkedInfo.by || "you"}. It stays here until you finish it.
        </div>
      )}

      {isIssued && (
        <div className="mt-4 border border-[#9A3412]/40 bg-[#FDF7F4] p-4" data-testid="amend-banner">
          <Label className="text-[11px] font-semibold uppercase tracking-wider text-[#9A3412]">
            Reason for amending #{existing.doc_number} *
          </Label>
          <Input
            className="mt-2 h-10 rounded-none bg-white"
            value={amendReason}
            onChange={(e) => setAmendReason(e.target.value)}
            placeholder="e.g. customer exchanged chain 4.630g for 4.890g"
            data-testid="amend-reason-input"
          />
          <p className="mt-2 text-xs text-muted-foreground">
            The invoice number stays the same. Every amendment is kept on the record.
          </p>
        </div>
      )}

      {rateChoice && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          data-testid="rate-choice-dialog"
        >
          <div className="w-full max-w-md border border-border bg-white p-6">
            <h2 className="text-base font-bold md:text-lg">Which gold rate applies?</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              This weight is changing on an invoice that was already issued. Keep the rate the
              customer booked, or charge today's rate on the new weight?
            </p>
            <div className="mt-5 space-y-2">
              <Button
                type="button"
                variant="outline"
                className="h-11 w-full justify-between rounded-none"
                onClick={() => {
                  updateItem(rateChoice.idx, {
                    gross_weight: rateChoice.weight,
                    rate: rateChoice.booked,
                    rate_decided: true,
                  });
                  setRateChoice(null);
                }}
                data-testid="keep-booked-rate-button"
              >
                <span>Keep the booked rate</span>
                <span className="tabular font-semibold">{money(rateChoice.booked)} / gm</span>
              </Button>
              <Button
                type="button"
                className="h-11 w-full justify-between rounded-none bg-[#C5A059] text-[#0A0A0A] hover:bg-[#B38F4E]"
                onClick={() => {
                  updateItem(rateChoice.idx, {
                    gross_weight: rateChoice.weight,
                    rate: rateChoice.today,
                    rate_decided: true,
                  });
                  setRateChoice(null);
                }}
                data-testid="use-today-rate-button"
              >
                <span>Use today's rate</span>
                <span className="tabular font-semibold">{money(rateChoice.today)} / gm</span>
              </Button>
              <button
                type="button"
                className="w-full py-2 text-xs text-muted-foreground hover:text-foreground"
                onClick={() => setRateChoice(null)}
                data-testid="cancel-rate-choice"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_200px]">
        <div className="space-y-8">
          <CustomerHistory
            query={form.customer_phone.length >= 3 ? form.customer_phone : form.customer_name}
            onPick={(m) => {
              setForm({
                ...form,
                customer_name: m.customer_name || form.customer_name,
                customer_phone: m.customer_phone || form.customer_phone,
                customer_address: m.customer_address || form.customer_address,
                customer_email: m.customer_email || form.customer_email,
              });
              setConfirmDetails(true);
            }}
          />

          <section className="border border-border p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-base font-bold md:text-lg">Customer details</h2>
            </div>
            {confirmDetails && (
              <div
                className="mt-4 flex flex-wrap items-center justify-between gap-2 border border-[#C5A059] bg-[#FBF8F2] px-4 py-2.5 text-sm"
                data-testid="confirm-details-banner"
              >
                <span>
                  Returning customer — please check{" "}
                  <strong>{form.customer_name || "their"}</strong>'s details are still current
                  {!form.customer_address || !form.customer_email ? (
                    <>
                      {" "}
                      <span className="font-semibold text-[#9A3412]">
                        (no{" "}
                        {[
                          !form.customer_address && "address",
                          !form.customer_email && "email",
                        ]
                          .filter(Boolean)
                          .join(" or ")}{" "}
                        on file)
                      </span>
                    </>
                  ) : null}
                  .
                </span>
                <button
                  type="button"
                  onClick={() => setConfirmDetails(false)}
                  className="whitespace-nowrap text-xs font-semibold uppercase tracking-wider text-[#8A6A22] underline"
                  data-testid="confirm-details-ok"
                >
                  Looks right
                </button>
              </div>
            )}
            <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Customer name *">
                <Input
                  className={inputCls}
                  value={form.customer_name}
                  onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                  data-testid="customer-name-input"
                  required
                />
              </Field>
              <Field label="Phone *">
                <div className="flex gap-2">
                  <Select
                    value={form.customer_country_code}
                    onValueChange={(v) => setForm({ ...form, customer_country_code: v })}
                  >
                    <SelectTrigger
                      className="h-10 w-[110px] rounded-none"
                      data-testid="customer-country-code"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-none bg-white">
                      {COUNTRY_CODES.map(([code, name]) => (
                        <SelectItem key={code} value={code}>
                          {code} {name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    className={`${inputCls} flex-1`}
                    value={form.customer_phone}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        customer_phone: e.target.value.replace(/\D/g, ""),
                      })
                    }
                    inputMode="numeric"
                    data-testid="customer-phone-input"
                    required
                  />
                </div>
              </Field>
              <Field label="Address">
                <AddressAutocomplete
                  className={inputCls}
                  value={form.customer_address}
                  onChange={(v) => setForm({ ...form, customer_address: v })}
                  placeholder="Start typing the address"
                  testid="customer-address-input"
                />
              </Field>
              <Field label="Email">
                <Input
                  className={inputCls}
                  value={form.customer_email}
                  onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
                  data-testid="customer-email-input"
                />
              </Field>
              <Field label="Birthday (optional)">
                <Input
                  type="date"
                  className={inputCls}
                  value={form.customer_birthday}
                  onChange={(e) => setForm({ ...form, customer_birthday: e.target.value })}
                  data-testid="customer-birthday-input"
                />
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Worth asking — it puts them on the birthday offer list. Never required.
                </p>
              </Field>
            </div>
          </section>

          {isBuying && (
            <section className="border border-border p-5" data-testid="buying-details-section">
              <h2 className="text-base font-bold md:text-lg">Seller identification</h2>
              <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label="Date of birth">
                  <Input
                    type="date"
                    className={inputCls}
                    value={buying.date_of_birth}
                    onChange={(e) => setBuying({ ...buying, date_of_birth: e.target.value })}
                    data-testid="buying-dob-input"
                  />
                </Field>
                <Field label="ID number">
                  <Input
                    className={inputCls}
                    value={buying.id_number}
                    onChange={(e) => setBuying({ ...buying, id_number: e.target.value })}
                    data-testid="buying-id-input"
                  />
                </Field>
                <Field label="State of issue">
                  <Input
                    className={inputCls}
                    value={buying.state_of_issue}
                    onChange={(e) => setBuying({ ...buying, state_of_issue: e.target.value })}
                    data-testid="buying-state-input"
                  />
                </Field>
                <Field label="ID expiry">
                  <Input
                    type="date"
                    className={inputCls}
                    value={buying.expiry}
                    onChange={(e) => setBuying({ ...buying, expiry: e.target.value })}
                    data-testid="buying-expiry-input"
                  />
                </Field>
                <Field label="Dealer name">
                  <Input
                    className={inputCls}
                    value={buying.dealer_name}
                    onChange={(e) => setBuying({ ...buying, dealer_name: e.target.value })}
                    data-testid="buying-dealer-input"
                  />
                </Field>
              </div>
              <h3 className="mt-6 text-[11px] uppercase tracking-wider text-muted-foreground">
                What happened to this gold — internal, never printed
              </h3>
              <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label="What was done">
                  <select
                    className="h-11 w-full border border-input bg-white px-3 text-sm"
                    value={buying.outcome || "in_stock"}
                    onChange={(e) => setBuying({ ...buying, outcome: e.target.value })}
                    data-testid="buying-outcome-select"
                  >
                    <option value="in_stock">Still in stock</option>
                    <option value="melted">Melted</option>
                    <option value="refinery">Sent to refinery</option>
                    <option value="sold">Sold on</option>
                    <option value="scrapped">Scrapped</option>
                  </select>
                </Field>
                <Field label="On date">
                  <Input
                    type="date"
                    className={inputCls}
                    value={buying.outcome_date || ""}
                    onChange={(e) => setBuying({ ...buying, outcome_date: e.target.value })}
                    data-testid="buying-outcome-date"
                  />
                </Field>
                <div className="sm:col-span-2">
                  <Field label="Notes (melt batch, docket number, who did it)">
                    <Input
                      className={inputCls}
                      value={buying.outcome_notes || ""}
                      onChange={(e) => setBuying({ ...buying, outcome_notes: e.target.value })}
                      data-testid="buying-outcome-notes"
                    />
                  </Field>
                </div>
              </div>

              <h3 className="mt-6 text-[11px] uppercase tracking-wider text-muted-foreground">
                Disposal record (second-hand dealer) — fill in when the goods move on
              </h3>
              <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label="Disposal or transfer of goods">
                  <Input
                    className={inputCls}
                    value={buying.disposal_goods}
                    onChange={(e) => setBuying({ ...buying, disposal_goods: e.target.value })}
                    data-testid="buying-disposal-goods-input"
                  />
                </Field>
                <Field label="Date sold or transferred">
                  <Input
                    type="date"
                    className={inputCls}
                    value={buying.disposal_date}
                    onChange={(e) => setBuying({ ...buying, disposal_date: e.target.value })}
                    data-testid="buying-disposal-date-input"
                  />
                </Field>
                <Field label="Sale or disposal price (AUD)">
                  <Input
                    type="number"
                    step="0.01"
                    className={inputCls}
                    value={buying.disposal_price}
                    onChange={(e) => setBuying({ ...buying, disposal_price: e.target.value })}
                    data-testid="buying-disposal-price-input"
                  />
                </Field>
                <Field label="Name and address of buyer">
                  <Input
                    className={inputCls}
                    value={buying.disposal_buyer}
                    onChange={(e) => setBuying({ ...buying, disposal_buyer: e.target.value })}
                    data-testid="buying-disposal-buyer-input"
                  />
                </Field>
              </div>
            </section>
          )}

          {isLoan ? (
            <section className="border border-border p-5" data-testid="loan-details-section">
              <h2 className="text-base font-bold md:text-lg">Loan &amp; pledged goods</h2>
              <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label="Loan amount (AUD)">
                  <Input
                    type="number"
                    step="0.01"
                    className={inputCls}
                    value={loan.loan_amount}
                    onChange={(e) => setLoan({ ...loan, loan_amount: e.target.value })}
                    data-testid="loan-amount-input"
                  />
                </Field>
                <Field label="Interest rate (% per month)">
                  <Input
                    type="number"
                    step="0.01"
                    className={inputCls}
                    value={loan.interest_rate}
                    onChange={(e) => setLoan({ ...loan, interest_rate: e.target.value })}
                    data-testid="loan-interest-input"
                  />
                </Field>
                <Field label="Loan paid to customer by *">
                  <select
                    className={`${inputCls} bg-white`}
                    value={loan.paid_to_customer_by}
                    onChange={(e) =>
                      setLoan({ ...loan, paid_to_customer_by: e.target.value })
                    }
                    data-testid="loan-payout-method-select"
                  >
                    {LOAN_PAYOUT_METHODS.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Payout reference (transfer or cheque number)">
                  <Input
                    className={inputCls}
                    value={loan.payout_reference}
                    onChange={(e) => setLoan({ ...loan, payout_reference: e.target.value })}
                    data-testid="loan-payout-reference-input"
                  />
                </Field>
                <Field label="Customer ID number">
                  <Input
                    className={inputCls}
                    value={loan.id_number}
                    onChange={(e) => setLoan({ ...loan, id_number: e.target.value })}
                    data-testid="loan-id-input"
                  />
                </Field>
                <Field label="Maximum months">
                  <Input
                    type="number"
                    className={inputCls}
                    value={loan.max_months}
                    onChange={(e) => setLoan({ ...loan, max_months: e.target.value })}
                    data-testid="loan-max-months-input"
                  />
                </Field>
                <Field label="Start date">
                  <Input
                    type="date"
                    className={inputCls}
                    value={loan.start_date}
                    onChange={(e) => setLoan({ ...loan, start_date: e.target.value })}
                    data-testid="loan-start-date-input"
                  />
                </Field>
                <Field label="End date (from maximum months)">
                  <Input
                    type="date"
                    className={inputCls}
                    value={loan.end_date}
                    onChange={(e) =>
                      setLoan({ ...loan, end_date: e.target.value, end_date_edited: true })
                    }
                    data-testid="loan-end-date-input"
                  />
                </Field>
                <Field label="Pledged goods description">
                  <Input
                    className={inputCls}
                    value={loan.description}
                    onChange={(e) => setLoan({ ...loan, description: e.target.value })}
                    placeholder="mix gold jewellery with stone"
                    data-testid="loan-description-input"
                  />
                </Field>
                <Field label="Weight">
                  <Input
                    className={inputCls}
                    value={loan.weight}
                    onChange={(e) => setLoan({ ...loan, weight: e.target.value })}
                    placeholder="18.700gm"
                    data-testid="loan-weight-input"
                  />
                </Field>
              </div>
              <div className="mt-5 border border-border bg-[#F8F9FA] px-4 py-3 text-sm">
                Interest per month:{" "}
                <span className="tabular font-bold" data-testid="loan-interest-per-month">
                  {money(interestPerMonth)}
                </span>
              </div>
            </section>
          ) : (
            <section className="border border-border" data-testid="line-items-section">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border bg-[#F8F9FA] px-4 py-3">
                <h2 className="text-sm font-bold uppercase tracking-wider">Line items</h2>
                <div className="flex items-center gap-2">
                  {!isBuying && (
                    <span className="hidden text-xs text-muted-foreground lg:inline" data-testid="todays-rate-label">
                      Today:{" "}
                      {KARATS.map((k) => `${k} $${rateFor(k).toFixed(2)}`).join("   ·   ")}
                    </span>
                  )}
                  {isTax && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="rounded-none"
                      onClick={applyRatesToAll}
                      data-testid="apply-todays-rate-button"
                    >
                      Apply today's rates
                    </Button>
                  )}
                  {isTax && (
                    <>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="rounded-none"
                        onClick={() => setNegotiateOpen(true)}
                        data-testid="negotiate-price-button"
                      >
                        Negotiate price
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="rounded-none text-[#8A6C36]"
                        onClick={openCalculator}
                        data-testid="open-calculator-button"
                      >
                        Ex-GST Calculator ↗
                      </Button>
                    </>
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="rounded-none"
                    onClick={() => setItems([...items, emptyItem()])}
                    data-testid="add-line-item-button"
                  >
                    <Plus className="mr-1 h-3.5 w-3.5" /> Add row
                  </Button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="calc-table w-full min-w-[860px] text-sm">
                  <thead className="bg-[#0A0A0A] text-white">
                    <tr>
                      <th className="sticky left-0 z-20 min-w-[140px] bg-[#0A0A0A] px-1.5 py-2 text-left font-semibold">
                        Description
                      </th>
                      {isTax ? (
                        <>
                          <th className="min-w-[60px] px-1 py-2 font-semibold">Karat</th>
                          <th className="min-w-[66px] px-1 py-2 text-right font-semibold">GWT</th>
                          <th className="min-w-[66px] px-1 py-2 text-right font-semibold">ST. WT</th>
                          <th className="min-w-[66px] px-1 py-2 text-right font-semibold">NWT</th>
                          <th className="min-w-[70px] px-1 py-2 text-right font-semibold">Rate / gm</th>
                          <th className="min-w-[62px] px-1 py-2 text-right font-semibold">S/C</th>
                          <th className="min-w-[52px] px-1 py-2 text-right font-semibold">M/C %</th>
                          <th className="min-w-[62px] px-1 py-2 text-right font-semibold">S/P</th>
                        </>
                      ) : (
                        <>
                          {isBuying && (
                            <th className="min-w-[70px] px-1.5 py-2 font-semibold">Metal</th>
                          )}
                          {isBuying && (
                            <th className="min-w-[90px] px-1.5 py-2 text-right font-semibold">
                              Weight (gm)
                            </th>
                          )}
                          <th className="min-w-[72px] px-1.5 py-2 text-right font-semibold">Qty</th>
                          <th className="min-w-[96px] px-1.5 py-2 text-right font-semibold">Price</th>
                        </>
                      )}
                      <th className="sticky right-8 z-20 min-w-[100px] bg-[#0A0A0A] px-1.5 py-2 text-right font-semibold">
                        Amount
                      </th>
                      <th className="sticky right-0 z-20 w-8 bg-[#0A0A0A]" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {items.map((it, idx) => (
                      <tr key={idx} data-testid={`line-item-row-${idx}`}>
                        <td className="sticky left-0 z-10 border-r border-border bg-white p-1 lg:p-0.5">
                          <Input
                            list="catalogue-options"
                            className="h-9 rounded-none"
                            value={it.description}
                            onChange={(e) => {
                              const name = e.target.value;
                              const match = catalogue.find((c) => c.name === name);
                              updateItem(idx, {
                                description: name,
                                ...(match && !isSilver(it.karat) ? { rate: rateFor(it.karat) } : {}),
                              });
                            }}
                            placeholder="Start typing to pick from catalogue"
                            data-testid={`item-description-${idx}`}
                          />
                          {descriptionWraps(it.description) && (
                            <p
                              className="mt-0.5 text-[11px] leading-tight text-[#B45309]"
                              data-testid={`item-description-wraps-${idx}`}
                            >
                              Prints on two lines — shorten to keep the row tidy
                            </p>
                          )}
                        </td>
                        {isTax ? (
                          <>
                            <td className="p-1 lg:p-0.5">
                              <Select
                                value={it.karat}
                                onValueChange={(v) =>
                                  updateItem(idx, { karat: v, rate: isSilver(v) ? 0 : rateFor(v) })
                                }
                              >
                                <SelectTrigger
                                  className="h-9 w-full min-w-0 rounded-none px-1.5 text-right tabular"
                                  data-testid={`item-karat-${idx}`}
                                >
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="rounded-none bg-white">
                                  {karats.map((k) => (
                                    <SelectItem key={k} value={k}>
                                      {k}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="p-1 lg:p-0.5">
                              <Input
                                type="number"
                                step="0.001"
                                className="h-9 w-full min-w-[48px] rounded-none px-1 text-right tabular"
                                value={it.gross_weight}
                                onChange={(e) => changeWeight(idx, e.target.value)}
                                onBlur={(e) => padWeight(idx, "gross_weight", e.target.value)}
                                data-testid={`item-gwt-${idx}`}
                              />
                            </td>
                            <td className="p-1 lg:p-0.5">
                              <Input
                                type="number"
                                step="0.001"
                                className="h-9 w-full min-w-[48px] rounded-none px-1 text-right tabular"
                                value={it.less_weight}
                                onChange={(e) => updateItem(idx, { less_weight: e.target.value })}
                                onBlur={(e) => padWeight(idx, "less_weight", e.target.value)}
                                data-testid={`item-less-${idx}`}
                              />
                            </td>
                            <td className="p-1 lg:p-0.5">
                              <div
                                className={`tabular flex h-9 w-full min-w-0 items-center justify-end border border-dashed px-1 text-sm font-semibold ${
                                  netWeight(it) < 0
                                    ? "border-destructive bg-destructive/10 text-destructive"
                                    : "border-input bg-muted"
                                }`}
                                data-testid={`item-nwt-${idx}`}
                                title="Net weight — gross weight less the stone weight"
                              >
                                {netWeight(it).toFixed(3)}
                              </div>
                            </td>
                            <td className="p-1 lg:p-0.5">
                              <Input
                                type="number"
                                step="0.01"
                                className="h-9 w-full min-w-[48px] rounded-none px-1 text-right tabular"
                                value={it.rate}
                                onChange={(e) => updateItem(idx, { rate: e.target.value })}
                                data-testid={`item-rate-${idx}`}
                              />
                            </td>
                            <td className="p-1 lg:p-0.5">
                              <Input
                                type="number"
                                step="0.01"
                                className="h-9 w-full min-w-[48px] rounded-none px-1 text-right tabular"
                                value={it.stone_cost}
                                onChange={(e) => updateItem(idx, { stone_cost: e.target.value })}
                                data-testid={`item-stone-cost-${idx}`}
                              />
                            </td>
                            <td className="p-1 lg:p-0.5">
                              <Input
                                type="number"
                                step="0.01"
                                className="h-9 w-full min-w-[48px] rounded-none px-1 text-right tabular"
                                value={it.labour_pct}
                                onChange={(e) => updateItem(idx, { labour_pct: e.target.value })}
                                disabled={Number(it.set_price) > 0}
                                title={Number(it.set_price) > 0
                                  ? "Clear the set price to use making charges %" : ""}
                                data-testid={`item-labour-${idx}`}
                              />
                            </td>
                            <td className="p-1 lg:p-0.5">
                              <Input
                                type="number"
                                step="0.01"
                                className="h-9 w-full min-w-[48px] rounded-none px-1 text-right tabular"
                                value={it.set_price}
                                onChange={(e) => updateItem(idx, { set_price: e.target.value })}
                                disabled={Number(it.labour_pct) > 0}
                                title={Number(it.labour_pct) > 0
                                  ? "Clear making charges % to use a set price" : ""}
                                data-testid={`item-set-price-${idx}`}
                              />
                            </td>
                          </>
                        ) : (
                          <>
                            {isBuying && (
                              <td className="p-1 lg:p-0.5">
                                <Select
                                  value={it.karat || ""}
                                  onValueChange={(v) => updateItem(idx, { karat: v })}
                                >
                                  <SelectTrigger
                                    className="h-9 w-full min-w-0 rounded-none px-1.5 text-right tabular"
                                    data-testid={`buy-metal-${idx}`}
                                  >
                                    <SelectValue placeholder="—" />
                                  </SelectTrigger>
                                  <SelectContent className="rounded-none bg-white">
                                    {karats.map((k) => (
                                      <SelectItem key={k} value={k}>
                                        {k}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </td>
                            )}
                            {isBuying && (
                              <td className="p-1 lg:p-0.5">
                                <Input
                                  type="number"
                                  step="0.001"
                                  className="h-9 w-full min-w-[48px] rounded-none px-1 text-right tabular"
                                  value={it.gross_weight}
                                  onChange={(e) =>
                                    updateItem(idx, {
                                      gross_weight: e.target.value,
                                      weight: e.target.value,
                                    })
                                  }
                                  onBlur={(e) =>
                                    padWeight(idx, "gross_weight", e.target.value, {
                                      weight: e.target.value === "" ? "" : Number(e.target.value).toFixed(3),
                                    })
                                  }
                                  data-testid={`item-weight-${idx}`}
                                />
                              </td>
                            )}
                            <td className="p-1 lg:p-0.5">
                              <Input
                                type="number"
                                step="0.01"
                                className="h-9 w-full min-w-[48px] rounded-none px-1 text-right tabular"
                                value={it.qty}
                                onChange={(e) => updateItem(idx, { qty: e.target.value })}
                                data-testid={`item-qty-${idx}`}
                              />
                            </td>
                            <td className="p-1 lg:p-0.5">
                              <Input
                                type="number"
                                step="0.01"
                                className="h-9 w-full min-w-0 rounded-none px-1.5 text-right tabular"
                                value={it.rate}
                                onChange={(e) => updateItem(idx, { rate: e.target.value })}
                                data-testid={`item-rate-${idx}`}
                              />
                            </td>
                          </>
                        )}
                        <td
                          className="tabular sticky right-8 z-10 whitespace-nowrap border-l border-border bg-white px-1.5 py-1.5 text-right font-semibold"
                          data-testid={`item-amount-${idx}`}
                        >
                          {money(lineAmount(it))}
                        </td>
                        <td className="sticky right-0 z-10 bg-white px-0.5">
                          <button
                            type="button"
                            onClick={() =>
                              setItems(
                                items.length === 1
                                  ? [emptyItem()]
                                  : items.filter((_, i) => i !== idx)
                              )
                            }                            className="p-1 text-muted-foreground transition-colors duration-150 hover:text-destructive"
                            data-testid={`remove-item-${idx}`}
                            aria-label="Remove row"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <datalist id="catalogue-options">
                {catalogue.map((c) => (
                  <option key={c.id} value={c.name} />
                ))}
              </datalist>
            </section>
          )}

          <section className="border border-border p-5" data-testid="customer-id-section">
            {accountDeposits.length > 0 && !isBuying && !isLoan && (
              <div
                className="mb-4 flex flex-wrap items-center justify-between gap-3 border border-[#166534] bg-[#F0FDF4] px-4 py-3 text-sm text-[#166534]"
                data-testid="credit-on-account-banner"
              >
                <span>
                  <strong>
                    This customer is holding{" "}
                    {money(
                      accountDeposits.reduce((sum, d) => sum + (Number(d.amount) || 0), 0)
                    )}{" "}
                    in credit
                  </strong>{" "}
                  from {accountDeposits.length} deposit
                  {accountDeposits.length > 1 ? "s" : ""} already paid. Put it on this
                  invoice before taking any more money.
                </span>
                <Button
                  type="button"
                  size="sm"
                  className="rounded-none bg-[#166534] font-semibold text-white hover:bg-[#12522A]"
                  onClick={pullAccountDeposits}
                  data-testid="credit-banner-pull-button"
                >
                  Pull from account
                </Button>
              </div>
            )}
            {(cash?.flagged || cash?.warning) && (
              <div
                className={`mb-4 border px-4 py-3 text-sm ${
                  cash.flagged
                    ? "border-[#9A3412] bg-[#FDF2EE] text-[#9A3412]"
                    : "border-[#C5A059] bg-[#FBF8F2] text-[#8A6A22]"
                }`}
                data-testid="cash-watch-banner"
              >
                {cash.flagged ? (
                  <>
                    <strong>AML: ID copies required.</strong> This customer has paid{" "}
                    {money(cash.all_time_cash)} in cash all time ({money(cash.fy_cash)} this
                    financial year)
                    {cash.single_over ? ", including a single cash payment over $10,000" : ""}.
                    {cash.ids_on_file
                      ? " ID copies are already on file."
                      : " Please take a copy of their photo ID."}
                  </>
                ) : (
                  <>
                    <strong>Approaching the $10,000 cash mark.</strong> Cash to date:{" "}
                    {money(cash.all_time_cash)} ({money(cash.fy_cash)} this financial year).
                  </>
                )}
              </div>
            )}
            {isBuying && (
              <div
                className={`mb-4 border px-4 py-3 text-sm ${
                  kyc?.has_kyc && !kyc.id_expired
                    ? "border-[#166534] bg-[#F0FDF4] text-[#166534]"
                    : "border-[#9A3412] bg-[#FDF2EE] text-[#9A3412]"
                }`}
                data-testid="kyc-warning"
              >
                {kyc?.has_kyc ? (
                  <>
                    KYC on file for {kyc.customer_name} ({kyc.status}, {kyc.submitted})
                    {kyc.id_expired ? " — their ID has expired, please take a fresh copy" : ""}
                  </>
                ) : (
                  <>
                    No KYC form on file for this phone number. Ask the customer to scan the
                    counter QR code before you finish the contract.
                  </>
                )}
              </div>
            )}
            <h2 className="text-base font-bold md:text-lg">
              Customer ID &amp; photos{isBuying ? " *" : ""}
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Take a photo of the customer's ID with the camera, or attach a JPG, PNG or PDF.
              Photos are shrunk automatically so they stay small but still readable.
              {isBuying ? " At least one is required on a buying contract." : ""}
            </p>
            <div className="mt-4">
              <PhotoCapture
                items={attachments}
                onChange={setAttachments}
                required={isBuying}
                testid="doc-photos"
              />
            </div>
          </section>

          <section className="border border-border p-5">
            <h2 className="text-base font-bold md:text-lg">Payment &amp; notes</h2>
            <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label={isBuying ? "Paying the customer by" : "Paid by"}>
                <Select
                  value={form.payment_method}
                  onValueChange={(v) =>
                    setForm({
                      ...form,
                      payment_method: v,
                      mark_paid: SETTLED_ON_SAVE.includes(v),
                    })
                  }
                >
                  <SelectTrigger
                    className={`${inputCls} ${
                      form.mark_paid &&
                      !isLoan &&
                      (totals.balance || 0) > 0.005 &&
                      !String(form.payment_method || "").trim()
                        ? "ring-2 ring-amber-400 border-amber-400"
                        : ""
                    }`}
                    data-testid="payment-method-select"
                  >
                    <SelectValue placeholder="Select a method" />
                  </SelectTrigger>
                  <SelectContent className="rounded-none bg-white">
                    {(isBuying ? BUYING_PAYMENT_METHODS : PAYMENT_METHODS).map((m) => (
                      <SelectItem key={m} value={m}>
                        {m}
                        {isBuying ? "" : feeLabel(feeRates, m)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {form.mark_paid &&
                  !isLoan &&
                  (totals.balance || 0) > 0.005 &&
                  !String(form.payment_method || "").trim() && (
                    <p
                      className="mt-1.5 text-xs text-amber-600"
                      data-testid="payment-method-hint"
                    >
                      You've ticked "Paid in full now" — pick how it was paid, or leave it blank
                      to record later.
                    </p>
                  )}
              </Field>
              {!isBuying && (
                <div
                  className="border-l-2 border-[#C5A059] bg-[#FBF8F2] p-3 text-xs text-muted-foreground sm:col-span-2"
                  data-testid="payment-fee-notes"
                >
                  Afterpay, Klarna and Zip Pay spending limits are set by each provider — if the
                  customer has no available limit, choose another method. In case of a refund the
                  processing fee is not refunded, as the bank charges it.
                </div>
              )}
              {kuberWaiting.length > 0 && (
                <div
                  className="rounded-md border-l-4 border-emerald-500 bg-emerald-50 p-3 text-sm font-semibold text-emerald-900 sm:col-span-2"
                  data-testid="kuber-waiting-banner"
                >
                  A Kuber payment of {money(kuberWaiting[0].settles)}
                  {kuberWaiting[0].settles !== kuberWaiting[0].amount
                    ? ` (you received ${money(kuberWaiting[0].amount)} after Kuber's ${money(
                        kuberWaiting[0].fee
                      )} fee)`
                    : ""}{" "}
                  is already waiting for invoice #{existing?.doc_number ?? reservedNumber}.
                  Don&apos;t record it by hand — it attaches to this invoice automatically
                  when you save or issue it.
                </div>
              )}
              {(() => {
                const suggest = kuberWaiting[0]?.method_label;
                const mismatch =
                  !isBuying &&
                  suggest &&
                  KUBER_FEE_METHODS.includes(suggest) &&
                  form.payment_method !== suggest;
                if (!mismatch) return null;
                const plain = suggest.replace("KUBER ", "");
                return (
                  <div
                    className="rounded-md border-l-4 border-amber-500 bg-amber-50 p-3 text-sm text-amber-900 sm:col-span-2"
                    data-testid="kuber-method-mismatch"
                  >
                    <p className="font-semibold">
                      This looks like a {plain} payment through Kuber.
                    </p>
                    <p className="mt-1 text-xs">
                      &quot;Paid by&quot; is set to{" "}
                      <span className="font-semibold">
                        {form.payment_method || "nothing yet"}
                      </span>
                      , but {plain} charges a higher fee — so the Platform Handling Fee printed
                      on the invoice won&apos;t match what the customer is actually charged. Switch
                      it so the disclosure is right. You can still save either way.
                    </p>
                    <button
                      type="button"
                      onClick={() =>
                        setForm({ ...form, payment_method: suggest, mark_paid: false })
                      }
                      className="mt-2 rounded bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-amber-700"
                      data-testid="kuber-method-mismatch-fix"
                    >
                      Set to {suggest}
                    </button>
                  </div>
                );
              })()}
              {form.payment_method?.startsWith("KUBER") && (
                <div
                  className="border-l-2 border-[#C5A059] bg-[#FBF8F2] p-3 text-xs font-semibold text-[#7A5C1E] sm:col-span-2"
                  data-testid="kuber-awaiting-note"
                >
                  {(existing?.doc_number ?? reservedNumber) ? (
                    <p className="mb-1.5 text-sm" data-testid="kuber-invoice-number-note">
                      In the Kuber app, type invoice number{" "}
                      <span className="tabular rounded bg-[#C5A059] px-1.5 py-0.5 text-sm font-bold text-[#0A0A0A]">
                        #{existing?.doc_number ?? reservedNumber}
                      </span>{" "}
                      exactly — this is how the payment matches to this invoice
                      automatically. One wrong digit and it won&apos;t link.
                    </p>
                  ) : (
                    <p className="mb-1.5 text-sm">
                      Save the invoice first to get its number, then type that exact number
                      in the Kuber app so the payment matches automatically.
                    </p>
                  )}
                  Awaiting Kuber confirmation — this invoice stays open. Tick &quot;Paid in
                  full now&quot; only once the funds show in the account.
                </div>
              )}
              {form.payment_method === "KUBER INSTALLMENTS" &&
                totals.total > KUBER_INSTALLMENT_LIMIT && (
                  <div
                    className="border-l-2 border-red-600 bg-red-50 p-3 text-xs font-semibold text-red-700 sm:col-span-2"
                    data-testid="kuber-limit-warning"
                  >
                    This invoice is over Kuber&apos;s $10,000 installments limit — the plan will
                    not be approved for the full amount. Split it with another method.
                  </div>
                )}
              {!isLoan && (
                <div className="sm:col-span-2">
                  <label className="flex cursor-pointer items-start gap-2.5 border border-border bg-[#FBF8F2] p-3 text-sm">
                    <input
                      type="checkbox"
                      className="mt-0.5 accent-[#C5A059]"
                      checked={form.mark_paid}
                      onChange={(e) => setForm({ ...form, mark_paid: e.target.checked })}
                      data-testid="mark-paid-checkbox"
                    />
                    <span>
                      <span className="font-semibold">Paid in full now</span> - settles whatever
                      is left with the method above the moment this is saved, so
                      you do not have to come back to it.
                    </span>
                  </label>
                </div>
              )}
              {isTax && PLATFORM_FEE_ENABLED && (
                <Field label="Platform handling fee override ($)">
                  <Input
                    type="number"
                    step="0.01"
                    className={inputCls}
                    placeholder="Leave blank for the standard fee"
                    value={form.platform_fee_override}
                    onChange={(e) =>
                      setForm({ ...form, platform_fee_override: e.target.value })
                    }
                    data-testid="fee-override-input"
                  />
                  <p
                    className="text-[11px] leading-snug text-[#9A3412]"
                    data-testid="fee-override-warning"
                  >
                    Changes the figure printed on the invoice only. It takes nothing off
                    what the customer pays and does not stop the provider charging their
                    fee. To actually save the customer the fee, use a fee-free method
                    (cash, PayID, bank transfer) or reduce the item price.
                  </p>
                </Field>
              )}
              {isTax && (
                <Field label="GST rate (%)">
                  <Input
                    type="number"
                    step="0.01"
                    className={inputCls}
                    value={form.gst_rate}
                    disabled={!canSetGst}
                    title={canSetGst ? "" : "Only a manager or the owner can change the GST rate"}
                    onChange={(e) => setForm({ ...form, gst_rate: e.target.value })}
                    data-testid="gst-rate-input"
                  />
                  {!canSetGst && (
                    <p className="mt-1 text-xs text-muted-foreground" data-testid="gst-rate-locked">
                      Set at 10%. A manager or the owner can change this.
                    </p>
                  )}
                  {canSetGst && Number(form.gst_rate) !== 10 && (
                    <p className="mt-1 text-xs text-destructive" data-testid="gst-rate-warning">
                      This invoice is not at 10% GST. Only do this if you mean to.
                    </p>
                  )}
                </Field>
              )}
              {!isLoan && !isBuying && (
                <div
                  className="sm:col-span-2 border-t border-border pt-4"
                  data-testid="deposits-section"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Deposits paid
                    </Label>
                    <div className="flex gap-2">
                      {accountDeposits.length > 0 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="rounded-none"
                          onClick={pullAccountDeposits}
                          data-testid="pull-account-deposits-button"
                        >
                          Pull {accountDeposits.length} from account
                        </Button>
                      )}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="rounded-none"
                        onClick={() =>
                          setDeposits([
                            ...deposits,
                            { entry_date: todayLabel(), amount: "", method: "CASH", note: "" },
                          ])
                        }
                        data-testid="add-deposit-button"
                      >
                        <Plus className="mr-1 h-3.5 w-3.5" /> Add deposit
                      </Button>
                    </div>
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">
                    A customer can pay in several instalments — add a row each time. Every
                    deposit prints on the invoice with its date and method.
                  </p>
                  <div className="mt-3 space-y-2">
                    {deposits.map((d, i) => (
                      <div
                        key={i}
                        className="flex flex-wrap items-center gap-2"
                        data-testid={`deposit-row-${i}`}
                      >
                        <Input
                          className="h-10 w-[130px] rounded-none"
                          placeholder="Date"
                          value={d.entry_date}
                          onChange={(e) =>
                            setDeposits(
                              deposits.map((x, j) =>
                                j === i ? { ...x, entry_date: e.target.value } : x
                              )
                            )
                          }
                          data-testid={`deposit-date-${i}`}
                        />
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="Amount"
                          className="h-10 w-28 rounded-none text-right"
                          value={d.amount}
                          onChange={(e) =>
                            setDeposits(
                              deposits.map((x, j) =>
                                j === i ? { ...x, amount: e.target.value } : x
                              )
                            )
                          }
                          data-testid={`deposit-amount-${i}`}
                        />
                        <Select
                          value={d.method}
                          onValueChange={(v) =>
                            setDeposits(
                              deposits.map((x, j) => (j === i ? { ...x, method: v } : x))
                            )
                          }
                        >
                          <SelectTrigger
                            className="h-10 w-[230px] rounded-none"
                            data-testid={`deposit-method-${i}`}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="rounded-none bg-white">
                            {PAYMENT_METHODS.map((m) => (
                              <SelectItem key={m} value={m}>
                                {m}
                                {feeLabel(feeRates, m)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Input
                          className="h-10 w-[170px] rounded-none"
                          placeholder="Note (optional)"
                          value={d.note}
                          onChange={(e) =>
                            setDeposits(
                              deposits.map((x, j) => (j === i ? { ...x, note: e.target.value } : x))
                            )
                          }
                          data-testid={`deposit-note-${i}`}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-10 shrink-0 rounded-none whitespace-nowrap"
                          onClick={() =>
                            setDeposits(
                              deposits.map((x, j) =>
                                j === i
                                  ? { ...x, amount: fillBalance(x.method, x.amount), filled: true }
                                  : x
                              )
                            )
                          }
                          data-testid={`fill-balance-deposit-${i}`}
                        >
                          Fill balance
                        </Button>
                        <button
                          type="button"
                          onClick={() => setDeposits(deposits.filter((_, j) => j !== i))}
                          className="p-2 text-muted-foreground transition-colors duration-150 hover:text-destructive"
                          data-testid={`remove-deposit-${i}`}
                          aria-label="Remove deposit row"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                        {d.ledger_id && (
                          <span
                            className="border border-[#1F6F43] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[#1F6F43]"
                            data-testid={`deposit-from-account-${i}`}
                          >
                            From account credit
                          </span>
                        )}
                      </div>
                    ))}
                    {deposits.length === 0 && (
                      <p className="text-xs text-muted-foreground">No deposit taken yet.</p>
                    )}
                  </div>
                </div>
              )}
              {!isLoan && !isBuying && (
                <div className="sm:col-span-2 border-t border-border pt-4" data-testid="payments-section">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Split payments (optional)
                    </Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="rounded-none"
                      onClick={() => setPayments([...payments, { method: "CASH", amount: "", label: "" }])}
                      data-testid="add-payment-button"
                    >
                      <Plus className="mr-1 h-3.5 w-3.5" /> Add payment
                    </Button>
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">
                    Add a row per method (e.g. CASH $500 + CARD $1,000). Enter what the shop
                    receives — "Fill balance" puts in exactly what is left owing. The
                    provider's fee is added at their end, so never key it in on top.
                  </p>
                  <div className="mt-3 space-y-2">
                    {payments.map((p, i) => (
                      <div key={i} className="space-y-1">
                      <div className="flex items-center gap-2" data-testid={`payment-row-${i}`}>
                        <Select
                          value={p.method}
                          onValueChange={(v) =>
                            setPayments(payments.map((x, j) => (j === i ? { ...x, method: v } : x)))
                          }
                        >
                          <SelectTrigger className="h-10 w-[170px] rounded-none" data-testid={`payment-method-${i}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="rounded-none bg-white">
                            {PAYMENT_METHODS.map((m) => (
                              <SelectItem key={m} value={m}>
                                {m}
                                {feeLabel(feeRates, m)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="Amount"
                          className="h-10 w-32 rounded-none text-right"
                          value={p.amount}
                          onChange={(e) =>
                            setPayments(
                              payments.map((x, j) =>
                                j === i
                                  ? { ...x, amount: e.target.value, filled: false }
                                  : x
                              )
                            )
                          }
                          data-testid={`payment-amount-${i}`}
                        />
                        <Input
                          placeholder="Label on invoice (optional)"
                          className="h-10 flex-1 rounded-none"
                          value={p.label}
                          onChange={(e) =>
                            setPayments(
                              payments.map((x, j) => (j === i ? { ...x, label: e.target.value } : x))
                            )
                          }
                          data-testid={`payment-label-${i}`}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-10 shrink-0 rounded-none whitespace-nowrap"
                          onClick={() =>
                            setPayments(
                              payments.map((x, j) =>
                                j === i
                                  ? { ...x, amount: fillBalance(x.method, x.amount), filled: true }
                                  : x
                              )
                            )
                          }
                          data-testid={`fill-balance-payment-${i}`}
                        >
                          Fill balance
                        </Button>
                        <button
                          type="button"
                          onClick={() => setPayments(payments.filter((_, j) => j !== i))}
                          className="p-2 text-muted-foreground transition-colors duration-150 hover:text-destructive"
                          data-testid={`remove-payment-${i}`}
                          aria-label="Remove payment"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                      {Number(p.amount) > 0 && (
                        <div
                          className="pl-1 text-[11px] font-semibold text-[#7A5C1E]"
                          data-testid={`payment-charge-hint-${i}`}
                        >
                          Take {money(round2(p.amount))} {collectWording(p.method)}
                          {feeOn(feeRates, p.amount, p.method) > 0
                            ? ` — ${p.method} add their own ${money(feeOn(feeRates, p.amount, p.method))} fee at their end`
                            : " — no provider fee"}
                        </div>
                      )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {!isLoan && !isBuying && (
                <div
                  className="sm:col-span-2 border-t border-border pt-4"
                  data-testid="trade-in-section"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Old gold trading (part exchange)
                    </Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="rounded-none"
                      onClick={() =>
                        setTradeIns([
                          ...tradeIns,
                          {
                            description: "OLD GOLD TRADING",
                            amount: "",
                            weight: "",
                            rate: "",
                            wear_tear_pct: "",
                          },
                        ])
                      }
                      data-testid="add-trade-in-button"
                    >
                      <Plus className="mr-1 h-3.5 w-3.5" /> Add old gold
                    </Button>
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">
                    Enter just the agreed price, or fill in weight and rate (less any wear and
                    tear) to work it out. Either way it is deducted from what the customer owes.
                  </p>
                  <div className="mt-3 space-y-2">
                    {tradeIns.map((t, i) => (
                      <div
                        key={i}
                        className="flex flex-wrap items-center gap-2"
                        data-testid={`trade-in-row-${i}`}
                      >
                        <Input
                          placeholder="Description"
                          className="h-10 w-[190px] rounded-none"
                          value={t.description}
                          onChange={(e) =>
                            setTradeIns(
                              tradeIns.map((x, j) =>
                                j === i ? { ...x, description: e.target.value } : x
                              )
                            )
                          }
                          data-testid={`trade-in-description-${i}`}
                        />
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="Price $"
                          className="h-10 w-28 rounded-none text-right"
                          value={t.amount ?? ""}
                          disabled={Number(t.weight) > 0 && Number(t.rate) > 0}
                          onChange={(e) =>
                            setTradeIns(
                              tradeIns.map((x, j) => (j === i ? { ...x, amount: e.target.value } : x))
                            )
                          }
                          data-testid={`trade-in-amount-${i}`}
                        />
                        <Input
                          type="number"
                          step="0.001"
                          placeholder="Weight (gm)"
                          className="h-10 w-28 rounded-none text-right"
                          value={t.weight}
                          onChange={(e) =>
                            setTradeIns(
                              tradeIns.map((x, j) => (j === i ? { ...x, weight: e.target.value } : x))
                            )
                          }
                          data-testid={`trade-in-weight-${i}`}
                        />
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="Rate"
                          className="h-10 w-28 rounded-none text-right"
                          value={t.rate}
                          onChange={(e) =>
                            setTradeIns(
                              tradeIns.map((x, j) => (j === i ? { ...x, rate: e.target.value } : x))
                            )
                          }
                          data-testid={`trade-in-rate-${i}`}
                        />
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="Wear & tear %"
                          className="h-10 w-32 rounded-none text-right"
                          value={t.wear_tear_pct}
                          onChange={(e) =>
                            setTradeIns(
                              tradeIns.map((x, j) =>
                                j === i ? { ...x, wear_tear_pct: e.target.value } : x
                              )
                            )
                          }
                          data-testid={`trade-in-wear-${i}`}
                        />
                        <span
                          className="tabular w-28 text-right font-semibold"
                          data-testid={`trade-in-value-${i}`}
                        >
                          -{money(tradeInValue(t))}
                        </span>
                        <button
                          type="button"
                          onClick={() => setTradeIns(tradeIns.filter((_, j) => j !== i))}
                          className="p-2 text-muted-foreground transition-colors duration-150 hover:text-destructive"
                          data-testid={`remove-trade-in-${i}`}
                          aria-label="Remove old gold row"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <label className="mt-4 flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={!!form.wear_tear_explained}
                      onChange={(e) =>
                        setForm({ ...form, wear_tear_explained: e.target.checked })
                      }
                      data-testid="wear-tear-checkbox"
                    />
                    Wear and tear explained to the customer (prints on the invoice)
                  </label>
                </div>
              )}
              {isBullion && (
                <div className="sm:col-span-2 border border-border bg-[#FBF8F2] p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Payment reference printed on the invoice
                  </div>
                  <div className="mt-2 flex flex-wrap gap-4">
                    {["GOLD", "SILVER"].map((m) => (
                      <label
                        key={m}
                        className="flex cursor-pointer items-center gap-2 text-sm"
                      >
                        <input
                          type="radio"
                          name="bullion_metal"
                          className="accent-[#C5A059]"
                          checked={(form.bullion_metal || "GOLD") === m}
                          onChange={() => setForm({ ...form, bullion_metal: m })}
                          data-testid={`bullion-metal-${m.toLowerCase()}`}
                        />
                        Ref: {m === "GOLD" ? "Gold" : "Silver"} Bullion 9999
                      </label>
                    ))}
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground" data-testid="bullion-gst-note">
                    No GST is charged on this invoice. That is only correct for
                    investment-grade metal — 99.5% pure or finer (999, 9999). Anything
                    below that, such as 916 or 22kt, belongs on a tax invoice with GST.
                  </p>
                </div>
              )}
              <div className="sm:col-span-2">
                <Field label="Note on invoice">
                  <Textarea
                    className="rounded-none"
                    rows={2}
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    placeholder="e.g. can be exchanged by 17/08"
                    data-testid="notes-input"
                  />
                </Field>
              </div>
            </div>
          </section>
        </div>

        <aside className="h-fit lg:sticky lg:top-24" data-testid="live-summary">
          <div className="border border-border">
            <div className="border-b border-border bg-[#0A0A0A] px-3 py-2">
              <h2 className="text-[11px] font-bold uppercase tracking-wider text-white">
                Live summary
              </h2>
            </div>
            <div className="divide-y divide-border text-xs">
              {isLoan ? (
                <>
                  <Row label="Loan amount" value={money(loan.loan_amount)} testid="summary-loan-amount" />
                  <Row label="Interest / month" value={money(interestPerMonth)} testid="summary-loan-interest" />
                  <Row label="Pledged weight" value={loan.weight || "-"} testid="summary-loan-weight" />
                </>
              ) : (
                <>
                  {isTax && (
                    <Row
                      label="Weight"
                      value={`${totals.weight.toFixed(3)} gm`}
                      testid="summary-weight"
                    />
                  )}
                  <Row label="Items" value={money(totals.itemsTotal)} testid="summary-items-total" />
                  <Row label="Subtotal" value={money(totals.subtotal)} testid="summary-subtotal" />
                  {isTax && (
                    <Row
                      label={`GST ${Number(form.gst_rate) || 0}%`}
                      value={money(totals.gst)}
                      testid="summary-gst"
                    />
                  )}
                  {!isBuying && PLATFORM_FEE_ENABLED && (
                    <Row
                      label="Handling fee"
                      value={money(totals.platformFee)}
                      testid="summary-platform-fee"
                    />
                  )}
                  <div className="flex items-center justify-between gap-2 bg-[#C5A059] px-3 py-2 font-bold">
                    <span>{isBuying ? "Payable" : "Total"}</span>
                    <span className="tabular" data-testid="summary-total">
                      {money(totals.total)}
                    </span>
                  </div>
                  {totals.tradeInTotal > 0 && (
                    <Row
                      label="Trade-in"
                      value={`-${money(totals.tradeInTotal)}`}
                      testid="summary-trade-in"
                    />
                  )}
                  {totals.depositTotal > 0 && (
                    <Row
                      label={deposits.filter((d) => Number(d.amount) > 0).length > 1
                        ? "Deposits"
                        : "Deposit"}
                      value={money(totals.depositTotal)}
                      testid="summary-deposit"
                    />
                  )}
                  <Row label="Paid" value={money(totals.paid)} testid="summary-paid" />
                  <div
                    className={`flex items-center justify-between gap-2 px-3 py-2 font-bold ${
                      totals.balance <= 0.005
                        ? "bg-[#F0FDF4] text-[#166534]"
                        : "bg-[#FDF2EE] text-[#9A3412]"
                    }`}
                  >
                    <span>
                      {totals.balance < -0.005
                        ? "In credit"
                        : Math.abs(totals.balance) <= 0.005 && totals.paid > 0
                          ? "Paid in full"
                          : "Balance due"}
                    </span>
                    <span className="tabular" data-testid="summary-balance">
                      {money(totals.balance < -0.005 ? -totals.balance : totals.balance)}
                    </span>
                  </div>
                  {!isBuying &&
                    (totals.chargeRows.length > 0 || totals.balance > 0.005) && (
                      <div
                        className="bg-[#0A0A0A] px-3 py-2.5 text-white"
                        data-testid="terminal-charge-block"
                      >
                        <div className="text-[10px] font-bold uppercase tracking-wider text-[#C5A059]">
                          What to charge now
                        </div>
                        {totals.chargeRows.length > 0 ? (
                          <>
                            {totals.chargeRows.map((r, i) => (
                              <div
                                key={i}
                                className="mt-1.5 flex items-baseline justify-between gap-2"
                                data-testid={`terminal-charge-row-${i}`}
                              >
                                <span className="text-[11px] text-white/70">
                                  {r.kind ? `${r.kind} · ` : ""}
                                  {r.method} {collectWording(r.method)}
                                </span>
                                <span className="tabular font-bold">{money(r.amount)}</span>
                              </div>
                            ))}
                            <div className="mt-2 flex items-baseline justify-between gap-2 border-t border-white/25 pt-2">
                              <span className="text-[11px] font-semibold">
                                Total of these rows
                              </span>
                              <span
                                className="tabular text-base font-bold text-[#C5A059]"
                                data-testid="terminal-charge-total"
                              >
                                {money(totals.chargeTotal)}
                              </span>
                            </div>
                            {totals.balance > 0.005 && (
                              <div
                                className="mt-1.5 bg-[#7F1D1D] px-2 py-1 text-[10px] font-bold leading-snug text-white"
                                data-testid="terminal-charge-shortfall"
                              >
                                {money(totals.balance)} of this invoice is still not
                                covered — add a row for it, or the sale saves part paid.
                              </div>
                            )}
                            {totals.balance < -0.005 && (
                              <div
                                className="mt-1.5 bg-[#7F1D1D] px-2 py-1 text-[10px] font-bold leading-snug text-white"
                                data-testid="terminal-charge-overpaid"
                              >
                                These rows are {money(-totals.balance)} more than the
                                invoice — the difference becomes account credit.
                              </div>
                            )}
                            <div className="mt-1 text-[10px] leading-snug text-white/60">
                              {totals.chargeFee > 0
                                ? `These are the amounts the shop receives. Provider fees (about ${money(totals.chargeFee)} in total) are added at their end — never key them in on top.`
                                : "No provider fees on these methods."}
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="mt-1.5 flex items-baseline justify-between gap-2">
                              <span className="text-[11px] text-white/70">
                                {form.payment_method} {collectWording(form.payment_method)}
                              </span>
                              <span
                                className="tabular text-lg font-bold text-[#C5A059]"
                                data-testid="terminal-charge-single"
                              >
                                {money(totals.balance)}
                              </span>
                            </div>
                            <div className="mt-1 text-[10px] leading-snug text-white/60">
                              {totals.platformFee > 0 ? (
                                <>
                                  Goods and GST only. {form.payment_method} add their own{" "}
                                  {money(totals.platformFee)} fee at their end — never add
                                  it yourself.
                                </>
                              ) : (
                                "No provider fee on this method."
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    )}
                </>
              )}
            </div>
          </div>
          <details className="mt-3 text-[11px] text-muted-foreground">
            <summary className="cursor-pointer select-none" data-testid="summary-help-toggle">
              How these add up
            </summary>
            <p className="mt-2 leading-snug">
              {isTax
                ? "Amount = (NWT x rate) + M/C % of that + S/C + S/P. Use either M/C % or S/P on a line, not both. NWT is gross weight less the stone weight. GST is charged on the goods only — the handling fee is the provider's and is never added to the total."
                : "GST is not applied to gold bullion sales or buying contracts."}
            </p>
          </details>
        </aside>
      </div>
      {isTax && (
        <NegotiatePriceDialog
          open={negotiateOpen}
          onOpenChange={setNegotiateOpen}
          currentTotal={totals.total}
          currentSubtotal={totals.subtotal}
          gstRate={form.gst_rate === "" || form.gst_rate == null ? 10 : Number(form.gst_rate)}
          onApply={applyNegotiatedDiscount}
        />
      )}
    </form>
  );
}

const Row = ({ label, value, testid }) => (
  <div className="flex items-center justify-between gap-2 px-3 py-2">
    <span className="text-muted-foreground">{label}</span>
    <span className="tabular font-semibold" data-testid={testid}>
      {value}
    </span>
  </div>
);
